import { useState, useCallback, memo } from 'react';
import { cn, Textarea } from '@divlab/divanui';
import { nanoid } from 'nanoid';

import validationSchema from './schema';
import * as ModalWindows from '@Stores/ModalWindows';
import InputPhone from '@Components/InputPhone';
import { useRequest } from '@Contexts/Request';
import useTranslation from '@Queries/useTranslation';
import { useDeps } from '@Contexts/DI';
import Link from '@Navigation/Link';
import Form from '@UI/Form';
import FormItem from '@UI/FormItem';
import Input from '@UI/Input';
import Button from '@UI/Button';
import styles from './FeedbackForm.module.css';

import type { ApiResponse } from '@Types/Api';
import type { FC, HTMLAttributes } from 'react';

export interface FeedbackFormProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  mail: string;
}

const FeedbackForm: FC<FeedbackFormProps> = (props) => {
  const { className, mail, ...restProps } = props;
  const { isBlr, isRus } = useRequest();
  const { t } = useTranslation();
  const { analytics } = useDeps();

  const needPhone = isBlr || isRus;
  const [loading, setLoading] = useState(false);
  const [formKey, setFormKey] = useState<string>(nanoid());

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    setLoading(true);
  }, []);

  const handleError = useCallback(() => {
    setLoading(false);

    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  }, []);

  const handleResponse = useCallback(
    (response: ApiResponse) => {
      setLoading(false);

      if (response.ok === true) {
        analytics.dispatchEvent('forms', {
          formName: 'cooperation',
          data: {
            action: 'send',
            title: '',
          },
        });

        ModalWindows.open('Info', { view: 'success', variant: 'manager-callback' });
      } else {
        handleError();
      }

      // TODO Нужно пересмотреть решение. Сейчас мы обновляем ключ для очистки полей формы после отправки.
      // Хотелось бы иметь возможность очистки формы непосредственно в компоненте Form
      setFormKey(nanoid());
    },
    [handleError, analytics],
  );

  return (
    <div {...restProps} className={cn(styles.feedback, className)}>
      <div className={styles.wrapper}>
        <h2 className={styles.heading}>{t('ui.feedback-form.title')}</h2>
        <div className={styles.description}>
          {t('ui.feedback-form.description.1')}
          <Link target='_blank' view='native' to={`mailto:${mail}`}>
            {` ${mail} `}
          </Link>
          {t('ui.feedback-form.description.2')}
        </div>
        <Form
          key={formKey}
          className={styles.form}
          action='/backend/b2b/send-letter?form=b2b'
          method='POST'
          name='feedback-form'
          validationSchemaBuilder={validationSchema}
          onCriticalError={handleError}
          onSubmit={handleSubmit}
          onResponse={handleResponse}
        >
          <input hidden name='mailTo' defaultValue={mail} />

          <div className={styles.formWrapper}>
            <FormItem>
              <Input
                className={styles.input}
                type='text'
                placeholder={t('ui.form-fields.name')}
                name='name'
              />
            </FormItem>
            {needPhone && (
              <FormItem>
                <InputPhone
                  className={styles.input}
                  type='text'
                  placeholder={`${t('ui.form-fields.phone')}*`}
                  name='phone'
                />
              </FormItem>
            )}

            <FormItem>
              <Input
                className={styles.input}
                type='text'
                placeholder={`${t('ui.form-fields.email')}*`}
                name='mailFrom'
              />
            </FormItem>
            <FormItem>
              <Textarea
                className={styles.inputText}
                placeholder={`${t('ui.form-fields.text')}*`}
                name='message'
              />
            </FormItem>
            <Button type='submit' className={styles.button} waiting={loading}>
              {t('ui.feedback-form.button-submit')}
            </Button>
          </div>
        </Form>
        <span className={styles.info}>{t('ui.not-oferta')}</span>
      </div>
    </div>
  );
};

export default memo(FeedbackForm);
